import { Checkbox, Icon, ImageFallback, Shadow, Text, HStack, VStack } from '@genial/components';
import { BoxRadius, Color, Spacing, Typography } from '@genial/design-tokens';
import { Pressable } from 'native-base';
import React, { useCallback } from 'react';

import { WithProductId } from '../../..';
import { CustomItemTagObject } from '../../../../../../../../store/ProducManagement/types';
import { ItemTag } from '../../../../ItemTag';
import { getParamsDetailsPerProducts, ProductFieldProps } from '../../../constants';

interface ProductMobileCardProps<T extends WithProductId> {
  fields: ProductFieldProps<T>[];
  item: T;
  onSelect?: (value: string) => void;
  selectedItems?: string[];
  onDetails: (productId: number, managerImage: string, productName: string) => void;
  productType: 'funds' | 'fixedIncome';
}

export const ProductMobileCard = <T extends WithProductId>({
  fields = [],
  item,
  onSelect,
  selectedItems,
  onDetails,
  productType,
}: ProductMobileCardProps<T>) => {
  const productItemId = item.productId.toString() || '';

  const isSelected = selectedItems?.find((selectedItem) => selectedItem === productItemId) || false;

  const handleSelect = useCallback(
    (selectedValue: string) => {
      onSelect?.(selectedValue);
    },
    [onSelect]
  );

  const renderItem = useCallback((itemLabel: string, itemValue: unknown) => {
    if (itemLabel === 'Bloqueios') {
      return (
        <HStack
          w="full"
          justifyContent="space-between"
          alignItems="center"
          space={Spacing['inline.quarck']}
          borderBottomColor={Color['neutral.lightest']}
          borderBottomWidth="1px"
          pb={Spacing['inline.nano']}>
          <Text variant="regular" color={Color['neutral.medium']}>
            {itemLabel}
          </Text>
          <ItemTag data={(itemValue as unknown as CustomItemTagObject[]) || []} />
        </HStack>
      );
    }

    return (
      <HStack
        w="full"
        justifyContent="space-between"
        alignItems="center"
        space={Spacing['inline.quarck']}
        borderBottomColor={Color['neutral.lightest']}
        borderBottomWidth="1px"
        pb={Spacing['inline.nano']}>
        <Text variant="regular" color={Color['neutral.medium']}>
          {itemLabel}
        </Text>
        <Text variant="regular" color={Color['neutral.black']}>
          {String(itemValue) || '-'}
        </Text>
      </HStack>
    );
  }, []);

  return (
    <Shadow testId="shadow" level={1} borderRadius="8px">
      <VStack
        space={Spacing['inline.xxxs']}
        p={Spacing['inline.xxs']}
        justifyContent="space-between"
        alignItems="center">
        <HStack
          w="full"
          justifyContent="space-between"
          alignItems="center"
          space={Spacing['inline.xxs']}>
          <ImageFallback
            src={item.managerImage}
            alt="logo"
            width="54px"
            height="48px"
            testID="product-img"
            resizeMode="cover"
            borderRadius={BoxRadius.sm}
            resizeMethod="scale"
          />
          <Checkbox
            optionPadding={{ bottom: 0, right: 0 }}
            options={[{ label: '', value: productItemId || '' }]}
            onChange={handleSelect}
            testId={`checkbox-${productItemId}`}
            selectedValues={isSelected ? [productItemId || ''] : []}
          />
        </HStack>

        <VStack w="full" space={Spacing['inline.nano']}>
          {fields.map((field) => {
            return renderItem(field.label, item[field.key]);
          })}
        </VStack>

        <HStack
          w="full"
          justifyContent="flex-end"
          alignItems="center"
          space={Spacing['inline.nano']}>
          <Pressable
            testID="button-item"
            onPress={() => {
              const { productId, managerImage, productName } = getParamsDetailsPerProducts(
                item,
                productType
              );
              onDetails(productId, managerImage, productName);
            }}>
            <HStack alignItems="center" space={Spacing['inline.nano']}>
              <Text
                color={'brand.tertiary.principal'}
                variant="semibold"
                fontSize={Typography['fontsize.md']}>
                DETALHES
              </Text>
              <Icon name="ArrowRight" size={'xxxs'} color={Color['brand.tertiary.principal']} />
            </HStack>
          </Pressable>
        </HStack>
      </VStack>
    </Shadow>
  );
};
